import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Slider,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Container,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  AutoFixHigh as AIIcon,
  Psychology as PsychologyIcon,
  Speed as SpeedIcon,
  Tune as TuneIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Api as ApiIcon,
  MusicNote as TikTokIcon,
  Help as HelpIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { emailService, EmailConfig } from '../services/emailService';
import { useTheme } from '../contexts/ThemeContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`config-tabpanel-${index}`}
      aria-labelledby={`config-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ConfigPage: React.FC = () => {
  const userData = useSelector((state: RootState) => state.user.data);
  const [currentTab, setCurrentTab] = useState(0);
  
  // Estados para configurações da IA
  const [aiEnabled, setAiEnabled] = useState(true);
  const [aiModel, setAiModel] = useState('gpt-4');
  const [aiTemperature, setAiTemperature] = useState(0.7);
  const [customPrompt, setCustomPrompt] = useState('');
  const [autoImprove, setAutoImprove] = useState(true);
  const [maxTokens, setMaxTokens] = useState(2000);
  const [aiPersonality, setAiPersonality] = useState('professional');

  // Estados para configurações gerais
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [whatsappNotifications, setWhatsappNotifications] = useState(true);
  const [language, setLanguage] = useState('pt-BR');
  const [twoFactorAuth, setTwoFactorAuth] = useState(false);
  const [autoReply, setAutoReply] = useState(true);
  const [timezone, setTimezone] = useState('America/Sao_Paulo');

  // Estados para configurações de comunicação
  const [emailSignature, setEmailSignature] = useState('');
  const [whatsappTemplate, setWhatsappTemplate] = useState('');
  const [autoFollowUp, setAutoFollowUp] = useState(true);
  const [followUpDays, setFollowUpDays] = useState(3);

  // Estados para credenciais de APIs
  const [whatsappApiKey, setWhatsappApiKey] = useState('');
  const [whatsappApiSecret, setWhatsappApiSecret] = useState('');
  const [instagramApiKey, setInstagramApiKey] = useState('');
  const [instagramApiSecret, setInstagramApiSecret] = useState('');
  const [facebookApiKey, setFacebookApiKey] = useState('');
  const [facebookApiSecret, setFacebookApiSecret] = useState('');
  const [tiktokApiKey, setTiktokApiKey] = useState('');
  const [tiktokApiSecret, setTiktokApiSecret] = useState('');
  const [showSecrets, setShowSecrets] = useState(false);

  // Estado para controlar o diálogo de ajuda
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [currentApiHelp, setCurrentApiHelp] = useState<'whatsapp' | 'instagram' | 'facebook' | 'tiktok' | null>(null);

  const [emailConfig, setEmailConfig] = useState<EmailConfig>({
    smtpServer: '',
    smtpPort: 587,
    emailUser: '',
    emailPassword: '',
    useSSL: false
  });

  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [emailTestStatus, setEmailTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [emailTestMessage, setEmailTestMessage] = useState('');

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const [isLoading, setIsLoading] = useState(false);

  const { darkMode, toggleDarkMode } = useTheme();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSaveConfig = () => {
    // Aqui implementaremos a lógica para salvar as configurações
    console.log('Configurações salvas');
  };

  const getApiHelpContent = (api: 'whatsapp' | 'instagram' | 'facebook' | 'tiktok') => {
    switch (api) {
      case 'whatsapp':
        return {
          title: 'Como configurar a API do WhatsApp Business',
          steps: [
            '1. Acesse business.facebook.com',
            '2. Crie uma conta Business Manager',
            '3. Adicione seu número do WhatsApp Business',
            '4. Vá para Configurações > WhatsApp > API',
            '5. Gere suas credenciais de API'
          ]
        };
      case 'instagram':
        return {
          title: 'Como configurar a API do Instagram',
          steps: [
            '1. Acesse developers.facebook.com',
            '2. Crie um aplicativo',
            '3. Adicione o produto Instagram ao seu aplicativo',
            '4. Configure as permissões necessárias',
            '5. Obtenha suas credenciais de API'
          ]
        };
      case 'facebook':
        return {
          title: 'Como configurar a API do Facebook',
          steps: [
            '1. Acesse developers.facebook.com',
            '2. Crie um novo aplicativo',
            '3. Selecione as permissões necessárias',
            '4. Complete a verificação do aplicativo',
            '5. Obtenha suas credenciais de API'
          ]
        };
      case 'tiktok':
        return {
          title: 'Como configurar a API do TikTok',
          steps: [
            '1. Acesse developers.tiktok.com',
            '2. Crie uma conta de desenvolvedor',
            '3. Crie um novo aplicativo',
            '4. Configure as permissões necessárias',
            '5. Obtenha suas credenciais de API'
          ]
        };
    }
  };

  const handleOpenHelp = (api: 'whatsapp' | 'instagram' | 'facebook' | 'tiktok') => {
    setCurrentApiHelp(api);
    setHelpDialogOpen(true);
  };

  const handleEmailConfigChange = (field: keyof EmailConfig) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.value;
    
    setEmailConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTestEmail = async () => {
    try {
      setIsLoading(true);
      await emailService.testEmailConfig(emailConfig);
      showSnackbar('Email de teste enviado com sucesso', 'success');
    } catch (error) {
      showSnackbar('Erro ao enviar email de teste', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEmailConfig = async () => {
    try {
      setIsLoading(true);
      await emailService.saveEmailConfig(emailConfig);
      showSnackbar('Configurações de email salvas com sucesso', 'success');
    } catch (error) {
      showSnackbar('Erro ao salvar configurações de email', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  useEffect(() => {
    loadEmailConfig();
  }, []);

  const loadEmailConfig = async () => {
    try {
      const config = await emailService.getEmailConfig();
      if (config) {
        setEmailConfig(config);
      }
    } catch (error) {
      showSnackbar('Erro ao carregar configurações de email', 'error');
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Configurações
        </Typography>

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              icon={<TuneIcon />} 
              label="Geral" 
              id="config-tab-0"
              aria-controls="config-tabpanel-0"
            />
            <Tab 
              icon={<AIIcon />} 
              label="Inteligência Artificial" 
              id="config-tab-1"
              aria-controls="config-tabpanel-1"
            />
            <Tab 
              icon={<NotificationsIcon />} 
              label="Notificações" 
              id="config-tab-2"
              aria-controls="config-tabpanel-2"
            />
            <Tab 
              icon={<SecurityIcon />} 
              label="Segurança" 
              id="config-tab-3"
              aria-controls="config-tabpanel-3"
            />
            <Tab 
              icon={<EmailIcon />} 
              label="Email" 
              id="config-tab-4"
              aria-controls="config-tabpanel-4"
            />
            <Tab 
              icon={<WhatsAppIcon />} 
              label="Comunicação" 
              id="config-tab-5"
              aria-controls="config-tabpanel-5"
            />
            <Tab 
              icon={<ApiIcon />} 
              label="APIs" 
              id="config-tab-6"
              aria-controls="config-tabpanel-6"
            />
          </Tabs>

          {/* Tab Configurações Gerais */}
          <TabPanel value={currentTab} index={0}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LanguageIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Configurações Gerais</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Idioma</InputLabel>
                <Select
                  value={language}
                  label="Idioma"
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  <MenuItem value="pt-BR">Português (Brasil)</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Español</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Fuso Horário</InputLabel>
                <Select
                  value={timezone}
                  label="Fuso Horário"
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <MenuItem value="America/Sao_Paulo">Brasília (GMT-3)</MenuItem>
                  <MenuItem value="America/Manaus">Manaus (GMT-4)</MenuItem>
                  <MenuItem value="America/Belem">Belém (GMT-3)</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={darkMode}
                    onChange={toggleDarkMode}
                    color="primary"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'primary.main',
                      },
                    }}
                  />
                }
                label="Modo Escuro"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    color: darkMode ? 'text.primary' : 'text.primary',
                  },
                }}
              />
            </Paper>
          </TabPanel>

          {/* Tab Configurações da IA */}
          <TabPanel value={currentTab} index={1}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AIIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Configurações de Inteligência Artificial</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={aiEnabled}
                    onChange={(e) => setAiEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label="Ativar IA"
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Modelo de IA</InputLabel>
                <Select
                  value={aiModel}
                  label="Modelo de IA"
                  onChange={(e) => setAiModel(e.target.value)}
                >
                  <MenuItem value="gpt-4">GPT-4</MenuItem>
                  <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                  <MenuItem value="claude-2">Claude 2</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>Temperatura da IA</Typography>
                <Slider
                  value={aiTemperature}
                  onChange={(_, value) => setAiTemperature(value as number)}
                  min={0}
                  max={1}
                  step={0.1}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Prompt Personalizado"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                margin="normal"
                helperText="Instruções específicas para a IA"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={autoImprove}
                    onChange={(e) => setAutoImprove(e.target.checked)}
                    color="primary"
                  />
                }
                label="Melhorar Automaticamente"
                sx={{ mb: 2 }}
              />

              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>Máximo de Tokens</Typography>
                <Slider
                  value={maxTokens}
                  onChange={(_, value) => setMaxTokens(value as number)}
                  min={100}
                  max={4000}
                  step={100}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Personalidade da IA</InputLabel>
                <Select
                  value={aiPersonality}
                  label="Personalidade da IA"
                  onChange={(e) => setAiPersonality(e.target.value)}
                >
                  <MenuItem value="professional">Profissional</MenuItem>
                  <MenuItem value="friendly">Amigável</MenuItem>
                  <MenuItem value="creative">Criativa</MenuItem>
                </Select>
              </FormControl>
            </Paper>
          </TabPanel>

          {/* Tab Notificações */}
          <TabPanel value={currentTab} index={2}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NotificationsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Configurações de Notificações</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={notificationsEnabled}
                    onChange={(e) => setNotificationsEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label="Ativar Notificações"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    color="primary"
                  />
                }
                label="Notificações por Email"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={whatsappNotifications}
                    onChange={(e) => setWhatsappNotifications(e.target.checked)}
                    color="primary"
                  />
                }
                label="Notificações por WhatsApp"
                sx={{ mb: 2 }}
              />
            </Paper>
          </TabPanel>

          {/* Tab Segurança */}
          <TabPanel value={currentTab} index={3}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Configurações de Segurança</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={twoFactorAuth}
                    onChange={(e) => setTwoFactorAuth(e.target.checked)}
                    color="primary"
                  />
                }
                label="Autenticação em Dois Fatores"
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                color="primary"
                startIcon={<SecurityIcon />}
                sx={{ mt: 2 }}
              >
                Alterar Senha
              </Button>
            </Paper>
          </TabPanel>

          {/* Tab Email */}
          <TabPanel value={currentTab} index={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Configurações de Email</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <TextField
                fullWidth
                label="Servidor SMTP"
                value={emailConfig.smtpServer}
                onChange={handleEmailConfigChange('smtpServer')}
                margin="normal"
                helperText="Endereço do servidor SMTP (ex: smtp.gmail.com)"
              />

              <TextField
                fullWidth
                label="Porta SMTP"
                type="number"
                value={emailConfig.smtpPort}
                onChange={handleEmailConfigChange('smtpPort')}
                margin="normal"
                helperText="Porta do servidor SMTP (ex: 587)"
              />

              <TextField
                fullWidth
                label="Email"
                type="email"
                value={emailConfig.emailUser}
                onChange={handleEmailConfigChange('emailUser')}
                margin="normal"
                helperText="Email para envio automático"
              />

              <TextField
                fullWidth
                label="Senha"
                type={showEmailPassword ? 'text' : 'password'}
                value={emailConfig.emailPassword}
                onChange={handleEmailConfigChange('emailPassword')}
                margin="normal"
                helperText="Senha do email"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowEmailPassword(!showEmailPassword)}
                        edge="end"
                      >
                        {showEmailPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={emailConfig.useSSL}
                    onChange={handleEmailConfigChange('useSSL')}
                  />
                }
                label="Usar SSL"
              />

              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveEmailConfig}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Salvar Configurações'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleTestEmail}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Testar Email'}
                </Button>
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Configurações de Email de Saída</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Assinatura de Email"
                value={emailSignature}
                onChange={(e) => setEmailSignature(e.target.value)}
                margin="normal"
                helperText="Esta assinatura será incluída em todos os seus emails"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={autoReply}
                    onChange={(e) => setAutoReply(e.target.checked)}
                    color="primary"
                  />
                }
                label="Respostas Automáticas"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={autoFollowUp}
                    onChange={(e) => setAutoFollowUp(e.target.checked)}
                    color="primary"
                  />
                }
                label="Follow-up Automático"
                sx={{ mb: 2 }}
              />

              {autoFollowUp && (
                <Box sx={{ mt: 2 }}>
                  <Typography gutterBottom>
                    Dias para Follow-up
                  </Typography>
                  <Slider
                    value={followUpDays}
                    onChange={(_, value) => setFollowUpDays(value as number)}
                    min={1}
                    max={7}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    sx={{ mb: 3 }}
                  />
                </Box>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveConfig}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Salvar Configurações'}
                </Button>
              </Box>
            </Paper>
          </TabPanel>

          {/* Tab Comunicação */}
          <TabPanel value={currentTab} index={5}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WhatsAppIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">Configurações do WhatsApp</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Mensagem de Boas-vindas"
                value={whatsappTemplate}
                onChange={(e) => setWhatsappTemplate(e.target.value)}
                margin="normal"
                helperText="Mensagem que será enviada automaticamente para novos contatos"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={autoReply}
                    onChange={(e) => setAutoReply(e.target.checked)}
                    color="primary"
                  />
                }
                label="Respostas Automáticas"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={autoFollowUp}
                    onChange={(e) => setAutoFollowUp(e.target.checked)}
                    color="primary"
                  />
                }
                label="Follow-up Automático"
                sx={{ mb: 2 }}
              />

              {autoFollowUp && (
                <Box sx={{ mt: 2 }}>
                  <Typography gutterBottom>
                    Dias para Follow-up
                  </Typography>
                  <Slider
                    value={followUpDays}
                    onChange={(_, value) => setFollowUpDays(value as number)}
                    min={1}
                    max={7}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    sx={{ mb: 3 }}
                  />
                </Box>
              )}

              <Button
                variant="contained"
                color="primary"
                startIcon={<WhatsAppIcon />}
                sx={{ mt: 2 }}
              >
                Testar Conexão WhatsApp
              </Button>
            </Paper>
          </TabPanel>

          {/* Tab APIs */}
          <TabPanel value={currentTab} index={6}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ApiIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Credenciais de API</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              {/* WhatsApp API */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WhatsAppIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="subtitle1">WhatsApp Business API</Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="API Key"
                  value={whatsappApiKey}
                  onChange={(e) => setWhatsappApiKey(e.target.value)}
                  margin="normal"
                  helperText="Chave de API do WhatsApp Business"
                  type={showSecrets ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSecrets(!showSecrets)}
                          edge="end"
                        >
                          {showSecrets ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="API Secret"
                  value={whatsappApiSecret}
                  onChange={(e) => setWhatsappApiSecret(e.target.value)}
                  margin="normal"
                  helperText="Chave secreta da API do WhatsApp Business"
                  type={showSecrets ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSecrets(!showSecrets)}
                          edge="end"
                        >
                          {showSecrets ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleOpenHelp('whatsapp')}
                    startIcon={<HelpIcon />}
                  >
                    Como configurar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveConfig}
                    disabled={isLoading}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Salvar Credenciais'}
                  </Button>
                </Box>
              </Box>

              {/* Instagram API */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <InstagramIcon sx={{ mr: 1, color: 'error.main' }} />
                  <Typography variant="subtitle1">Instagram API</Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="API Key"
                  value={instagramApiKey}
                  onChange={(e) => setInstagramApiKey(e.target.value)}
                  margin="normal"
                  helperText="Chave de API do Instagram"
                  type={showSecrets ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSecrets(!showSecrets)}
                          edge="end"
                        >
                          {showSecrets ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="API Secret"
                  value={instagramApiSecret}
                  onChange={(e) => setInstagramApiSecret(e.target.value)}
                  margin="normal"
                  helperText="Chave secreta da API do Instagram"
                  type={showSecrets ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSecrets(!showSecrets)}
                          edge="end"
                        >
                          {showSecrets ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleOpenHelp('instagram')}
                    startIcon={<HelpIcon />}
                  >
                    Como configurar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveConfig}
                    disabled={isLoading}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Salvar Credenciais'}
                  </Button>
                </Box>
              </Box>

              {/* Facebook API */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <FacebookIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1">Facebook API</Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="API Key"
                  value={facebookApiKey}
                  onChange={(e) => setFacebookApiKey(e.target.value)}
                  margin="normal"
                  helperText="Chave de API do Facebook"
                  type={showSecrets ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSecrets(!showSecrets)}
                          edge="end"
                        >
                          {showSecrets ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="API Secret"
                  value={facebookApiSecret}
                  onChange={(e) => setFacebookApiSecret(e.target.value)}
                  margin="normal"
                  helperText="Chave secreta da API do Facebook"
                  type={showSecrets ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSecrets(!showSecrets)}
                          edge="end"
                        >
                          {showSecrets ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleOpenHelp('facebook')}
                    startIcon={<HelpIcon />}
                  >
                    Como configurar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveConfig}
                    disabled={isLoading}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Salvar Credenciais'}
                  </Button>
                </Box>
              </Box>

              {/* TikTok API */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TikTokIcon sx={{ mr: 1, color: 'text.primary' }} />
                  <Typography variant="subtitle1">TikTok API</Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="API Key"
                  value={tiktokApiKey}
                  onChange={(e) => setTiktokApiKey(e.target.value)}
                  margin="normal"
                  helperText="Chave de API do TikTok"
                  type={showSecrets ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSecrets(!showSecrets)}
                          edge="end"
                        >
                          {showSecrets ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="API Secret"
                  value={tiktokApiSecret}
                  onChange={(e) => setTiktokApiSecret(e.target.value)}
                  margin="normal"
                  helperText="Chave secreta da API do TikTok"
                  type={showSecrets ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSecrets(!showSecrets)}
                          edge="end"
                        >
                          {showSecrets ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleOpenHelp('tiktok')}
                    startIcon={<HelpIcon />}
                  >
                    Como configurar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveConfig}
                    disabled={isLoading}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Salvar Credenciais'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </TabPanel>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ConfigPage; 